import * as React from 'react'
import createPersistedState from 'use-persisted-state'
import i18n, { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, Language, SUPPORTED_LANGUAGES } from './i18n'

// The active language lives in the same shared, persisted store as the favorite/
// watch-later toggles. use-persisted-state keeps its store in module scope and is
// a shared dependency (portal importmap), so writing here reaches every pilet in
// the same tab immediately — and the choice survives a reload.
const useLanguageState = createPersistedState(LANGUAGE_STORAGE_KEY)

/**
 * The shell's single source of truth for the active UI language.
 *
 * State is the persisted bucket; an effect mirrors every change into the shared
 * i18next instance, which re-renders every pilet's `useTranslation` in the new
 * locale. Read `language`, call `setLanguage`, render `languages` — nothing else
 * needs to know how the switch propagates.
 */
export function useLanguage () {
  const [language, setLanguage] = useLanguageState<Language>(
    (i18n.language as Language) ?? DEFAULT_LANGUAGE
  )

  React.useEffect(() => {
    if (language && language !== i18n.language) {
      i18n.changeLanguage(language)
    }
  }, [language])

  return {
    language,
    setLanguage,
    languages: SUPPORTED_LANGUAGES
  }
}
